import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { formatBatteryTestedDate } from '@redevicer/shared'

const POPOVER_EST_HEIGHT = 340

function parseIsoDate(iso: string): Date | null {
  if (!iso?.trim()) return null
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function monthStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 12, 0, 0, 0)
}

function addMonths(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1, 12, 0, 0, 0)
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function isSameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
}

function weekdayLabels(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' })
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 1 + i, 12)
    return fmt.format(d).replace(/\./g, '').slice(0, 2)
  })
}

type PopoverCoords = {
  left: number
  width: number
  top: number
  above: boolean
}

type Props = {
  value: string
  onChange: (iso: string) => void
  max?: string
  ariaLabel: string
  /** Prefer opening above the field (sell builder footer overlay). */
  preferAbove?: boolean
  /** Inside segmented pill bar (battery tested field). */
  layout?: 'default' | 'segment'
}

export default function BuilderDateField({
  value,
  onChange,
  max,
  ariaLabel,
  preferAbove = false,
  layout = 'default',
}: Props) {
  const { t, i18n } = useTranslation()
  const id = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<PopoverCoords | null>(null)

  const maxDate = useMemo(() => {
    const parsed = parseIsoDate(max ?? toIsoDate(new Date()))
    return parsed ?? new Date()
  }, [max])

  const today = useMemo(() => {
    const n = new Date()
    return new Date(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0, 0)
  }, [])

  const selected = useMemo(() => parseIsoDate(value), [value])
  const [viewMonth, setViewMonth] = useState(() => monthStart(selected ?? today))

  useEffect(() => {
    if (selected) setViewMonth(monthStart(selected))
  }, [value])

  const syncCoords = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const gap = 8
    const spaceBelow = window.innerHeight - rect.bottom - 12
    const spaceAbove = rect.top - 12
    const above =
      preferAbove || (spaceBelow < POPOVER_EST_HEIGHT && spaceAbove > spaceBelow)
    const top = above ? rect.top - gap : rect.bottom + gap
    setCoords({
      left: rect.left,
      width: Math.max(rect.width, 280),
      top,
      above,
    })
  }, [preferAbove])

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    syncCoords()
  }, [open, syncCoords, viewMonth])

  useEffect(() => {
    if (!open) return
    function onScroll() {
      syncCoords()
    }
    window.addEventListener('resize', syncCoords)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', syncCoords)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, syncCoords])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      const target = e.target as Node
      if (rootRef.current?.contains(target)) return
      if (target instanceof Element && target.closest('.builder-date-popover--portal')) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const weeks = useMemo(() => {
    const first = monthStart(viewMonth)
    let mondayOffset = first.getDay()
    mondayOffset = mondayOffset === 0 ? 6 : mondayOffset - 1
    const gridStart = new Date(first)
    gridStart.setDate(first.getDate() - mondayOffset)
    const days: Date[] = []
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart)
      d.setDate(gridStart.getDate() + i)
      d.setHours(12, 0, 0, 0)
      days.push(d)
    }
    const rows: Date[][] = []
    for (let i = 0; i < 42; i += 7) rows.push(days.slice(i, i + 7))
    return rows
  }, [viewMonth])

  const weekdays = useMemo(() => weekdayLabels(i18n.language), [i18n.language])

  const canPrevMonth = viewMonth.getFullYear() > 2015

  const canNextMonth =
    viewMonth.getFullYear() < maxDate.getFullYear() ||
    (viewMonth.getFullYear() === maxDate.getFullYear() && viewMonth.getMonth() < maxDate.getMonth())

  function pickDay(d: Date) {
    if (d > maxDate) return
    onChange(toIsoDate(d))
    setOpen(false)
  }

  const display =
    formatBatteryTestedDate(value, i18n.language) ?? t('batteryTransparency.form.testedPlaceholder')

  const popover =
    open && coords ? (
      <div
        className={`builder-date-popover builder-date-popover--portal${
          coords.above ? ' builder-date-popover--above' : ''
        }`}
        role="dialog"
        aria-labelledby={`${id}-title`}
        style={{
          position: 'fixed',
          left: coords.left,
          width: coords.width,
          top: coords.top,
          zIndex: 10050,
          transform: coords.above ? 'translateY(-100%)' : undefined,
        }}
      >
        <div className="builder-date-popover__head">
          <button
            type="button"
            className="builder-date-popover__nav"
            disabled={!canPrevMonth}
            aria-label={t('batteryTransparency.form.calendarPrev')}
            onClick={() => setViewMonth((m) => addMonths(m, -1))}
          >
            ‹
          </button>
          <p id={`${id}-title`} className="builder-date-popover__title">
            {viewMonth.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
          </p>
          <button
            type="button"
            className="builder-date-popover__nav"
            disabled={!canNextMonth}
            aria-label={t('batteryTransparency.form.calendarNext')}
            onClick={() => setViewMonth((m) => addMonths(m, 1))}
          >
            ›
          </button>
        </div>

        <div className="builder-date-popover__weekdays" aria-hidden>
          {weekdays.map((label, idx) => (
            <span key={`${label}-${idx}`} className="builder-date-popover__weekday">
              {label}
            </span>
          ))}
        </div>

        <div className="builder-date-popover__grid">
          {weeks.map((row) =>
            row.map((d) => {
              const outside = !isSameMonth(d, viewMonth)
              const disabled = d > maxDate
              const isSelected = selected ? sameDay(d, selected) : false
              const isToday = sameDay(d, today)
              return (
                <button
                  key={toIsoDate(d)}
                  type="button"
                  className={`builder-date-popover__day${
                    outside ? ' builder-date-popover__day--outside' : ''
                  }${disabled ? ' builder-date-popover__day--disabled' : ''}${
                    isSelected ? ' builder-date-popover__day--selected' : ''
                  }${isToday && !isSelected ? ' builder-date-popover__day--today' : ''}`}
                  disabled={disabled}
                  onClick={() => pickDay(d)}
                >
                  {d.getDate()}
                </button>
              )
            }),
          )}
        </div>

        <div className="builder-date-popover__foot">
          <button
            type="button"
            className="builder-date-popover__foot-btn"
            onClick={() => {
              onChange('')
              setOpen(false)
            }}
          >
            {t('batteryTransparency.form.calendarClear')}
          </button>
          <button
            type="button"
            className="builder-date-popover__foot-btn builder-date-popover__foot-btn--primary"
            onClick={() => {
              const pick = today > maxDate ? maxDate : today
              onChange(toIsoDate(pick))
              setOpen(false)
            }}
          >
            {t('batteryTransparency.form.calendarToday')}
          </button>
        </div>
      </div>
    ) : null

  return (
    <div
      ref={rootRef}
      className={`builder-date-field${open ? ' builder-date-field--open' : ''}${
        layout === 'segment' ? ' builder-date-field--segment' : ''
      }`}
    >
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="builder-date-field__trigger"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`builder-date-field__value${!value ? ' builder-date-field__value--placeholder' : ''}`}>
          {display}
        </span>
        <span className="builder-date-field__icon" aria-hidden />
      </button>

      {typeof document !== 'undefined' && popover ? createPortal(popover, document.body) : null}
    </div>
  )
}
